import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HandCoins } from "lucide-react";
import {
  COMMISSION_ROUTES,
  commissionsByUserResponseSchema,
  formatPrice,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Badge, Input, Select } from "@/shared/ui/core";
import { EmptyState, SkeletonTable } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  FilterBar,
  DEFAULT_PER_PAGE,
  Pagination,
  Td,
  Th,
  Tr,
} from "@/features/admin/components";
import { formatOrderDateTime } from "@/features/admin/orders/format";

interface Filters {
  query: string;
  /** "" = todas las monedas. */
  currency: string;
  page: number;
  perPage: number;
}

function listByUser(filters: Filters) {
  const params = new URLSearchParams();
  params.set("page", String(filters.page));
  params.set("perPage", String(filters.perPage));
  if (filters.query.trim()) params.set("query", filters.query.trim());
  if (filters.currency) params.set("currency", filters.currency);
  return authFetch(
    `${COMMISSION_ROUTES.adminByUser}?${params.toString()}`,
    commissionsByUserResponseSchema,
    { baseUrl: "" },
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Comisiones — el libro AGRUPADO POR PERSONA (Anahí, 29-ago).
 *
 * "Se deben mostrar las comisiones por usuarios, o sea por correos, y
 * cuando ingreses al usuario ahí muestras su historia."
 *
 * Era un renglón por COMISIÓN, así que la misma socia aparecía cinco
 * veces con cinco montos y no había forma de leer cuánto lleva ganado
 * sin sumar a mano. Ahora cada fila es una persona con su TOTAL, lo
 * cobrado y lo que se le debe; el desglose bono por bono vive en su
 * expediente, que es donde tiene sentido leerlo.
 *
 * El correo va junto al nombre porque dos socias pueden llamarse igual
 * y el correo es lo que no se repite.
 *
 * El PLAN (los porcentajes) vive en su propia pantalla; esta es el
 * resultado.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminCommissionsPage() {
  return (
    <AdminGuard>
      <CommissionsScreen />
    </AdminGuard>
  );
}

function CommissionsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [currency, setCurrency] = useState("");
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o
   * 20, con CINCO por defecto. Al cambiarlo se vuelve a la página 1 —
   * la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);

  const filters: Filters = { query, currency, page, perPage };
  const commissions = useQuery({
    queryKey: ["admin", "commissions", "por-persona", filters],
    queryFn: () => listByUser(filters),
    placeholderData: (previous) => previous,
  });

  const items = commissions.data?.items ?? [];
  const total = commissions.data?.total ?? 0;

  /* Los totales de la PÁGINA, para el resumen de arriba — POR MONEDA. */
  const totales = new Map<string, { generado: number; debido: number }>();
  for (const row of items) {
    const acc = totales.get(row.currency) ?? { generado: 0, debido: 0 };
    acc.generado += row.totalCents;
    acc.debido += row.owedCents;
    totales.set(row.currency, acc);
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Comisiones")}
        description={t("El libro: cada comisión registrada por el motor, con su estado.")}
      />

      {/*
        ── El corte mensual del bono de red (14-sep-2026) ──────────────
        La tarjeta existe (`MonthlyCutCard`) pero Anahí pidió NO
        enseñarla todavía: el corte corre solo el día 1 por Cloud
        Scheduler. Cuando quiera correrlo o simularlo a mano, se
        descomenta esta línea.

        <MonthlyCutCard />
      */}

      <DataTableFrame aria-busy={commissions.isFetching}>
        {/* ── Los filtros dentro de la card, como en todo el panel ────── */}
        {/*
          ── Se busca por CORREO o nombre (Anahí, 29-ago) ─────────────
          Los filtros de bono y estado eran de la vista por comisión: en
          una lista de personas, "solo BIR" o "solo pagadas" daría
          totales que no cuadran con la suma de nadie. Aquí lo que se
          busca es una persona.
        */}
        <FilterBar
          actions={
            <div className="flex flex-wrap items-center gap-2">
              {[...totales.entries()].map(([moneda, acc]) => (
                <span
                  key={moneda}
                  className="rounded-ds-pill border border-ds-border bg-white px-3 py-1 text-[12px] text-ds-ink-3"
                >
                  <span className="font-semibold text-ds-ink">{moneda}</span> · {t("Generado")}:{" "}
                  <span className="font-semibold text-ds-ink">{formatPrice(acc.generado, moneda as "MXN" | "USD")}</span>
                  {" · "}
                  {t("Por pagar")}:{" "}
                  <span className="font-semibold text-ds-ink">{formatPrice(acc.debido, moneda as "MXN" | "USD")}</span>
                </span>
              ))}
            </div>
          }
        >
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Buscar")}
            <div className="w-72">
              <Input
                aria-label={t("Buscar")}
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Correo o nombre…")}
              />
            </div>
          </label>
          {/* Por MONEDA (Anahí, 14-sep): pesos y dólares son libros distintos. */}
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Moneda")}
            <div className="w-36">
              <Select
                aria-label={t("Moneda")}
                value={currency}
                onChange={(event) => {
                  setCurrency(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todas")}
                options={[
                  { value: "MXN", label: "MXN · pesos" },
                  { value: "USD", label: "USD · dólares" },
                ]}
              />
            </div>
          </label>
        </FilterBar>
        {commissions.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={6} columns={7} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<HandCoins className="h-5 w-5 text-ds-ink-3" />}
              title={t("Sin comisiones registradas")}
              description={t("El motor escribe aquí cada vez que una orden validada genera bonos.")}
            />
          </div>
        ) : (
          <DataTable caption={t("Libro de comisiones")}>
            <thead>
              <tr>
                <Th>{t("Persona")}</Th>
                <Th>{t("Moneda")}</Th>
                <Th align="right">{t("Comisiones")}</Th>
                <Th align="right">{t("Monto")}</Th>
                <Th align="right">{t("Pagado")}</Th>
                <Th align="right">{t("Por pagar")}</Th>
                <Th align="right">{t("Última")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <Tr
                  key={`${row.userId}-${row.currency}`}
                  /*
                   * A su EXPEDIENTE: ahí está la historia completa —cada
                   * bono, de qué compra salió, su estado y el pago—.
                   */
                  onClick={() => navigate(`/admin/commissions/u/${row.userId}`)}
                  className="cursor-pointer"
                >
                  <Td>
                    <p className="font-medium">{row.name}</p>
                    {row.email && <p className="text-[12px] text-ds-ink-3">{row.email}</p>}
                  </Td>
                  <Td>
                    <Badge variant={row.currency === "USD" ? "indigo" : "info"} size="sm">
                      {row.currency}
                    </Badge>
                  </Td>
                  <Td align="right" className="text-ds-ink-3">
                    {row.count}
                  </Td>
                  {/* EL total: lo que esa persona ha generado en toda su
                      historia, no el importe de un renglón suelto. */}
                  <Td align="right" className="font-semibold">
                    {formatPrice(row.totalCents, row.currency)}
                  </Td>
                  <Td align="right" className="text-ds-ink-3">
                    {formatPrice(row.paidCents, row.currency)}
                  </Td>
                  <Td align="right">
                    {row.owedCents > 0 ? (
                      <Badge variant="warning" size="sm" dot>
                        {formatPrice(row.owedCents, row.currency)}
                      </Badge>
                    ) : (
                      <span className="text-ds-ink-3">{formatPrice(0, row.currency)}</span>
                    )}
                  </Td>
                  <Td align="right" className="text-ds-ink-3">
                    {row.lastAt ? formatOrderDateTime(row.lastAt) : "—"}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination
            page={page}
            perPage={perPage}
            total={total}
            onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>

    </>
  );
}
